import type { AuditEvent } from '@commissionsight/sdk';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../api.js';
import { Button, Card, EmptyState, ICONS, Icon, inputClass } from '../components/ui.js';

const when = (t: number | string) =>
  new Date(t).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Coarse category for the filter dropdown, derived from the dotted action verb. */
function category(action: string): string {
  if (action === 'user.login') return 'Sign-in';
  if (action.startsWith('file') || action.startsWith('job') || action.includes('upload'))
    return 'Upload';
  if (action.includes('rate') || action.includes('expected') || action.includes('schedule'))
    return 'Rates';
  if (action.includes('member') || action.includes('team') || action.includes('invite'))
    return 'Team';
  if (action.startsWith('ai')) return 'AI';
  return 'Other';
}
const CATEGORIES = ['All', 'Sign-in', 'Upload', 'Rates', 'Team', 'AI', 'Other'];

/** The account audit / activity log — its own page (was buried in Profile), with
 *  category + text filtering. Sign-ins show IP / browser / OS. */
export function Activity() {
  const { client } = useAuth();
  const PAGE = 50;
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');

  const load = async (offset = 0) => {
    setLoading(true);
    try {
      const r = await client.listAudit({ limit: PAGE, offset });
      setEvents((prev) => (offset === 0 ? r.data : [...prev, ...r.data]));
      setHasMore(r.pagination?.hasMore ?? false);
    } catch {
      // keep whatever's already loaded
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    load();
  }, [client]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return events.filter((e) => {
      if (cat !== 'All' && category(e.action) !== cat) return false;
      if (!needle) return true;
      const m = (e.meta ?? {}) as { browser?: string; os?: string };
      return [e.action, e.actor, e.target, e.ip, m.browser, m.os]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [events, cat, q]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neutral-700 to-neutral-900 text-white shadow-sm">
          <Icon path={ICONS.list} size={20} />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Activity log</h1>
          <p className="text-sm text-neutral-500">
            Every account action — sign-ins (with IP, browser, OS), uploads, rate changes, team
            changes, and more.
          </p>
        </div>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            className={`${inputClass} w-40`}
            value={cat}
            onChange={(e) => setCat(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c === 'All' ? 'All activity' : c}
              </option>
            ))}
          </select>
          <input
            className={`${inputClass} flex-1`}
            placeholder="Search action, user, IP…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="text-xs text-neutral-400">
            {filtered.length} of {events.length}
          </span>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={ICONS.list}
            title={events.length === 0 ? 'No activity yet' : 'No matching activity'}
          />
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {filtered.map((e) => {
              const m = (e.meta ?? {}) as { browser?: string | null; os?: string | null };
              const ctx = [e.ip, m.browser, m.os].filter(Boolean).join(' · ');
              return (
                <div key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div className="min-w-0">
                    {e.action === 'user.login' ? (
                      <span className="font-medium">Signed in</span>
                    ) : (
                      <span className="font-mono font-medium">{e.action}</span>
                    )}
                    {e.target && (
                      <span className="ml-2 truncate font-mono text-xs text-neutral-400">
                        {e.target}
                      </span>
                    )}
                    {ctx && <div className="mt-0.5 text-xs text-neutral-400">{ctx}</div>}
                  </div>
                  <div className="shrink-0 text-right text-xs text-neutral-400">
                    <div>{e.actor}</div>
                    <div>{when(e.createdAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {hasMore && (
          <div className="mt-3 text-center">
            <Button variant="ghost" size="sm" loading={loading} onClick={() => load(events.length)}>
              Load more
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
